import { BoxAnnotation, ECoordinateMode } from 'scichart';
import type { SciChartSurface } from 'scichart';
import type { AnomalyBandWithForm, AnomalyBandsConfig } from './types';

const BAND_FILL = '#93C5FD40';
const BAND_STROKE = '#60A5FA';
const INCOMPLETE_BAND_FILL = '#80808040';
const INCOMPLETE_BAND_STROKE = '#808080';
const ANCHOR_BAND_FILL = '#3B82F666';
const ANCHOR_BAND_STROKE = '#2563EB';
const BAND_STROKE_THICKNESS = 1;
const SELECTED_BAND_STROKE_THICKNESS = 3;
const SELECTION_BOX_THICKNESS = 0;
const SELECTION_BOX_DELTA = 0;
const ANNOTATION_GRIP_RADIUS = 6;
const ANNOTATION_GRIP_FILL = '#FFFFFF';

const CRITICALITY_COLORS: Record<string, { fill: string; stroke: string }> = {
  Critical: { fill: '#dc262688', stroke: '#991b1b' },
  High: { fill: '#f9731688', stroke: '#c2410c' },
  Medium: { fill: '#eab30888', stroke: '#a16207' },
  Low: { fill: '#92D05088', stroke: '#6ea235' },
};

function normalizeBandRange(startTime: number, endTime: number): { startTime: number; endTime: number } {
  return {
    startTime: Math.min(startTime, endTime),
    endTime: Math.max(startTime, endTime),
  };
}

function clampValue(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

function getXAxisBounds(sciChartSurface: SciChartSurface): { min: number; max: number } | null {
  const xAxis = (sciChartSurface as any)?.xAxes?.get?.(0);
  const range = xAxis?.visibleRangeLimit ?? xAxis?.visibleRange;

  if (!range) {
    return null;
  }

  const min = Number(range.min);
  const max = Number(range.max);

  if (!Number.isFinite(min) || !Number.isFinite(max)) {
    return null;
  }

  return { min, max };
}

function applyNormalizedRangeToAnnotation(
  annotation: BoxAnnotation,
  sciChartSurface: SciChartSurface
): { startTime: number; endTime: number } {
  const bounds = getXAxisBounds(sciChartSurface);
  const rawStart = Number(annotation.x1);
  const rawEnd = Number(annotation.x2);
  const rawRange = normalizeBandRange(rawStart, rawEnd);
  let nextRange = rawRange;

  if (bounds) {
    const requestedWidth = rawRange.endTime - rawRange.startTime;
    const maxWidth = Math.max(0, bounds.max - bounds.min);
    const clampedWidth = Math.min(requestedWidth, maxWidth);

    let nextStart = rawRange.startTime;
    let nextEnd = rawRange.endTime;

    if (nextStart < bounds.min) {
      nextStart = bounds.min;
      nextEnd = bounds.min + clampedWidth;
    }

    if (nextEnd > bounds.max) {
      nextEnd = bounds.max;
      nextStart = bounds.max - clampedWidth;
    }

    nextRange = normalizeBandRange(
      clampValue(nextStart, bounds.min, bounds.max),
      clampValue(nextEnd, bounds.min, bounds.max)
    );
  }

  annotation.x1 = nextRange.startTime;
  annotation.x2 = nextRange.endTime;
  annotation.y1 = 0;
  annotation.y2 = 1;
  sciChartSurface.invalidateElement();

  return nextRange;
}

function isBandIncomplete(band: AnomalyBandWithForm): boolean {
  const hasStreams = Array.isArray(band.streams) && band.streams.length > 0;
  const hasCriticality = band.criticality != null && band.criticality !== '';
  return !hasStreams || !hasCriticality;
}

function getBandColors(band: AnomalyBandWithForm): { fill: string; stroke: string } {
  if (band.bandType === 'anchor') {
    return {
      fill: ANCHOR_BAND_FILL,
      stroke: ANCHOR_BAND_STROKE,
    };
  }

  if (isBandIncomplete(band)) {
    return {
      fill: INCOMPLETE_BAND_FILL,
      stroke: INCOMPLETE_BAND_STROKE,
    };
  }

  return CRITICALITY_COLORS[band.criticality ?? ''] ?? {
    fill: BAND_FILL,
    stroke: BAND_STROKE,
  };
}

export function attachAnomalyBands(
  sciChartSurface: SciChartSurface,
  _wasmContext?: any,
  config?: AnomalyBandsConfig,
  rootElement?: string | HTMLDivElement
): () => void {
  if (!config) return () => {};
  const bandAnnotations: Map<string, BoxAnnotation> = new Map();

  const syncBandsToSurface = (bands: AnomalyBandWithForm[], selectedBandId: string | null = null) => {
    // Remove bands that no longer exist
    bandAnnotations.forEach((ann, id) => {
      if (!bands.some((b) => b.id === id)) {
        sciChartSurface.annotations.remove(ann);
        try {
          (ann as any).delete?.();
        } catch {}
        bandAnnotations.delete(id);
      }
    });

    // Ongoing bands are open-ended: extend them to the chart's right edge so they read
    // as continuing rather than ending at the drawn date.
    const bounds = getXAxisBounds(sciChartSurface);

    // Add or update bands
    bands.forEach((band) => {
      const normalizedRange = normalizeBandRange(band.startTime, band.endTime);
      const endX = band.ongoing && band.bandType !== 'anchor' && bounds ? bounds.max : normalizedRange.endTime;
      const { fill, stroke } = getBandColors(band);
      const strokeThickness = selectedBandId === band.id ? SELECTED_BAND_STROKE_THICKNESS : BAND_STROKE_THICKNESS;

      let ann = bandAnnotations.get(band.id);
      if (!ann) {
        ann = new BoxAnnotation({
          x1: normalizedRange.startTime,
          x2: endX,
          yCoordinateMode: ECoordinateMode.Relative,
          y1: 0,
          y2: 1,
          fill,
          stroke,
          strokeThickness,
          selectionBoxStroke: stroke,
          selectionBoxThickness: SELECTION_BOX_THICKNESS,
          selectionBoxDelta: SELECTION_BOX_DELTA,
          annotationsGripsStroke: stroke,
          annotationsGripsFill: ANNOTATION_GRIP_FILL,
          annotationsGripsRadius: ANNOTATION_GRIP_RADIUS,
          isEditable: !config.readOnly,
          onDrag: () => {
            if (config.readOnly) {
              return;
            }
            const nextRange = applyNormalizedRangeToAnnotation(ann!, sciChartSurface);
            config!.setBands((prev) =>
              prev.map((b) =>
                b.id === band.id
                  ? { ...b, startTime: nextRange.startTime, endTime: nextRange.endTime }
                  : b
              )
            );
            emitSelectedBand();
          },
        });
        (ann as any).bandId = band.id;
        sciChartSurface.annotations.add(ann);
        bandAnnotations.set(band.id, ann);
      } else {
        ann.x1 = normalizedRange.startTime;
        ann.x2 = endX;
        ann.fill = fill;
        ann.stroke = stroke;
        ann.strokeThickness = strokeThickness;
        ann.selectionBoxStroke = stroke;
        ann.selectionBoxThickness = SELECTION_BOX_THICKNESS;
        ann.selectionBoxDelta = SELECTION_BOX_DELTA;
        ann.annotationsGripsStroke = stroke;
        ann.annotationsGripsFill = ANNOTATION_GRIP_FILL;
        ann.annotationsGripsRadius = ANNOTATION_GRIP_RADIUS;
        ann.isEditable = !config.readOnly;
      }
    });

    // Restore selection on this chart (e.g. when switching tabs)
    const adornerLayer = (sciChartSurface as any)?.adornerLayer;
    if (config.readOnly && adornerLayer) {
      adornerLayer.selectedAnnotation = undefined;
    } else if (adornerLayer && selectedBandId) {
      const annToSelect = bandAnnotations.get(selectedBandId);
      if (annToSelect) {
        adornerLayer.selectedAnnotation = annToSelect;
      }
    } else if (adornerLayer) {
      adornerLayer.selectedAnnotation = undefined;
    }

    sciChartSurface.invalidateElement();
  };

  const emitSelectedBand = () => {
    if (config.readOnly) {
      return;
    }
    const adornerLayer = (sciChartSurface as any)?.adornerLayer;
    const selected = adornerLayer?.selectedAnnotation as any;
    if (!selected) {
      if (config.keepSingleBandSelected && config.bands.length === 1) {
        const onlyBand = config.bands[0];
        const onlyAnnotation = bandAnnotations.get(onlyBand.id);
        if (onlyAnnotation && adornerLayer) {
          adornerLayer.selectedAnnotation = onlyAnnotation;
          config.setSelectedBandId(onlyBand.id);
          sciChartSurface.invalidateElement();
          return;
        }
      }

      config.setSelectedBandId(null);
      return;
    }
    const bandId = selected.bandId;
    // Always set selection when we have bandId from annotation - config.bands may be stale in closure
    config.setSelectedBandId(bandId);
  };

  const pointerUpHandler = () => {
    if (config.readOnly) {
      const adornerLayer = (sciChartSurface as any)?.adornerLayer;
      if (adornerLayer) {
        adornerLayer.selectedAnnotation = undefined;
      }
      sciChartSurface.invalidateElement();
      return;
    }

    const adornerLayer = (sciChartSurface as any)?.adornerLayer;
    const selected = adornerLayer?.selectedAnnotation as (BoxAnnotation & { bandId?: string }) | undefined;

    if (selected?.bandId) {
      const nextRange = applyNormalizedRangeToAnnotation(selected, sciChartSurface);
      config.setBands((prev) =>
        prev.map((band) =>
          band.id === selected.bandId
            ? { ...band, startTime: nextRange.startTime, endTime: nextRange.endTime }
            : band
        )
      );
    }

    emitSelectedBand();
  };
  const container =
    rootElement instanceof HTMLDivElement
      ? rootElement
      : rootElement
        ? document.getElementById(rootElement)
        : sciChartSurface.domCanvas2D?.parentElement ?? sciChartSurface.domCanvas2D;
  container?.addEventListener?.('pointerup', pointerUpHandler);

  const unregister = config.registerUpdater(syncBandsToSurface);
  syncBandsToSurface(config.bands, config.selectedBandId);

  return () => {
    container?.removeEventListener?.('pointerup', pointerUpHandler);
    bandAnnotations.forEach((ann) => {
      sciChartSurface.annotations.remove(ann);
      try {
        (ann as any).delete?.();
      } catch {}
    });
    bandAnnotations.clear();
    unregister();
  };
}
